import { _callBackend } from '@/_api/api';
import { APIResponsePromise, APIRequest } from '@/_types/api';
import { fileUpload } from './client-upload';
import { DropLink } from '@/_types/models/dropLink';
import { v4 as uuidv4 } from 'uuid';

export const notifyDropLinkUpload = async ({
  dropLinkId,
  folderName,
  email,
  phoneNumber,
  description,
  firstName,
  lastName,
}: APIRequest<{
  dropLinkId: number;
  folderName: string;
  email: string;
  phoneNumber?: string;
  description: string;
  firstName?: string;
  lastName?: string;
}>): APIResponsePromise =>
  _callBackend('/drop_links/notify_upload', 'POST', {
    drop_link_id: dropLinkId,
    folder_name: folderName,
    email,
    phone_number: phoneNumber,
    description,
    first_name: firstName,
    last_name: lastName,
  });

export const downloadUpload = async ({
  dropLinkId,
  uploadId,
  token,
}: APIRequest<{
  dropLinkId: number;
  uploadId: number;
  token: string;
}>): APIResponsePromise => {
  const response = await _callBackend(
    `/drop_links/${dropLinkId}/download_upload?upload_id=${uploadId}`,
    'POST',
    {
      drop_link_id: dropLinkId,
      upload_id: uploadId,
    },
    token,
    { returnResponseAsIs: true },
  );
  // Check if the response is an error (JSON)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const jsonData = await response.json();
    throw new Error(jsonData.error || 'Download failed');
  }

  // Both single and multiple files are now downloaded as blobs
  const blob = await response.blob();

  // Get filename from our custom header
  const filename =
    response.headers.get('X-File-Name') || 'drop-link-upload.zip';

  const url = globalThis?.URL?.createObjectURL(blob);
  const downloadLink: HTMLAnchorElement = document.createElement('a');

  downloadLink.href = url;
  downloadLink.setAttribute('download', filename);
  document.body.appendChild(downloadLink);
  downloadLink.click();

  return response;
};

export const deleteDropLinkUpload = async ({
  dropLinkId,
  uploadId,
  token,
}: APIRequest<{
  dropLinkId: number;
  uploadId: number;
  token: string;
}>): APIResponsePromise =>
  _callBackend(
    `/drop_links/${dropLinkId}/delete_upload?upload_id=${uploadId}`,
    'GET',
    undefined,
    token,
  );

export const toggleUploadAsSeen = async ({
  dropLinkId,
  uploadId,
  token,
}: APIRequest<{
  dropLinkId: number;
  uploadId: number;
  token: string;
}>): APIResponsePromise =>
  _callBackend(
    `/drop_links/${dropLinkId}/toggle_upload_as_seen?upload_id=${uploadId}`,
    'GET',
    undefined,
    token,
  );

export const getDropLinks = async ({
  token,
}: APIRequest<{
  token: string;
}>): APIResponsePromise => _callBackend('/drop_links', 'GET', undefined, token);

export const createDropLink = async ({
  token,
  name,
  hideSignInButton,
  subscribedUserIds,
  customInstruction,
}: APIRequest<{
  token: string;
  name: string;
  hideSignInButton: boolean;
  subscribedUserIds: number[];
  customInstruction?: string;
}>): APIResponsePromise =>
  _callBackend(
    '/drop_links',
    'POST',
    {
      name,
      hide_sign_in_button: hideSignInButton,
      subscribed_user_ids: subscribedUserIds,
      custom_instruction: customInstruction,
    },
    token,
  );

export const updateDropLink = async ({
  token,
  dropLink,
}: APIRequest<{
  token: string;
  dropLink: DropLink;
}>): APIResponsePromise =>
  _callBackend(`/drop_links/${dropLink.id}`, 'PUT', dropLink, token);

export const deleteDropLink = async ({
  id,
  token,
}: APIRequest<{
  id: number;
  token: string;
}>): APIResponsePromise =>
  _callBackend(`/drop_links/${id}`, 'DELETE', undefined, token);

export const getDropLinkByHashId = async (hashId: string): APIResponsePromise =>
  _callBackend(`/drop_links/show_by_hash_id?hash_id=${hashId}`, 'GET');

export const getDropLinkUploads = async ({
  dropLinkId,
  token,
}: APIRequest<{
  dropLinkId: number;
  token: string;
}>): APIResponsePromise =>
  _callBackend(
    `/drop_links/${dropLinkId}/get_uploads`,
    'GET',
    undefined,
    token,
  );

export const uploadAndNotifyDropLinkUpload = async ({
  files,
  dropLinkId,
  email,
  phoneNumber,
  description,
  firstName,
  lastName,
  hashId,
}: APIRequest<{
  files: File[];
  dropLinkId: number;
  email: string;
  phoneNumber?: string;
  description: string;
  firstName?: string;
  lastName?: string;
  hashId: string;
}>): APIResponsePromise => {
  const folderLocation = uuidv4();

  // 1. Upload all files
  for (const file of files) {
    try {
      const { upload_link } = await _callBackend(
        `/drop_links/${dropLinkId}/get_upload_link_for_drop_link?folder_location=${folderLocation}&file_name=${file.name}&hash_id=${hashId}`,
        'GET',
      );
      if (upload_link) {
        await fileUpload(upload_link, false, file);
      }
    } catch (error) {
      console.error(error);
    }
  }

  // 2. Notify backend ONCE after all uploads
  return notifyDropLinkUpload({
    dropLinkId,
    folderName: folderLocation,
    email,
    phoneNumber,
    description,
    firstName,
    lastName,
  });
};

export const createTemplateDropLinks = async ({
  token,
}: APIRequest<{
  token: string;
}>): APIResponsePromise =>
  _callBackend(
    '/drop_links/create_template_drop_links',
    'POST',
    undefined,
    token,
  );
